import { useMemo, useSyncExternalStore } from 'react';

import * as AuthModule from '../../lib/auth/authContext';
import { isGreenfieldAdmin } from '../../lib/auth/greenfield';
import { useStudies } from './api/queries';

export interface UnifiedContractStudyOption {
  readonly contractCode: string;
  readonly studyCode: string;
  readonly title: string;
  readonly sponsorName: string;
  readonly croName: string;
  readonly centerName: string;
  readonly localityCityUf: string;
  readonly hourlyRateBrl: number;
}

const DEMO_CONTRACTS_CATALOG: readonly UnifiedContractStudyOption[] = [
  {
    contractCode: 'CTA-EPOCA-2026-BR',
    studyCode: 'EPOCA',
    title: 'Estudo EPOCA — Insuficiência Cardíaca Plurianual D0–D1440',
    sponsorName: 'AstraZeneca AB / Hcor',
    croName: 'IQVIA Brasil Pesquisa Clínica Ltda.',
    centerName: 'Centro GIYUS Curitiba/PR',
    localityCityUf: 'Curitiba/PR',
    hourlyRateBrl: 280,
  },
  {
    contractCode: 'CTA-SOLAIR-2026-INTL',
    studyCode: 'SOLAIR',
    title: 'Estudo SOLAIR — Asma Grave Eosinofílica & FeNO',
    sponsorName: 'IQVIA RDS Inc. (Exterior · USD)',
    croName: 'PPD / Thermo Fisher Clinical',
    centerName: 'Centro GIYUS Curitiba/PR',
    localityCityUf: 'Curitiba/PR',
    hourlyRateBrl: 310,
  },
  {
    contractCode: 'CTA-ARGON-2026-CH',
    studyCode: 'ARGON-301',
    title: 'Estudo ARGON-301 — Oncologia / Imunoterapia Basel',
    sponsorName: 'Roche Global Clinical Trials (Basel)',
    croName: 'ICON Clinical Research',
    centerName: 'Centro GIYUS Curitiba/PR',
    localityCityUf: 'Curitiba/PR',
    hourlyRateBrl: 340,
  },
];

const SEEDED_DEMO_STUDY_IDS = new Set([
  'study-001',
  'study-002',
  'study-003',
  'study-004',
  'study-005',
  '01926f3e-1000-7000-8000-000000000001',
  '01926f3e-1000-7000-8000-000000000002',
  '01926f3e-1000-7000-8000-000000000003',
]);

/**
 * Unifica Estudos = Contratos em toda a plataforma.
 * Em produção (`greenfield`), retorna exclusivamente os contratos/estudos reais cadastrados pelo usuário.
 */
export function useRegisteredContracts(): {
  readonly contracts: readonly UnifiedContractStudyOption[];
  readonly greenfield: boolean;
  readonly resolveContract: (codeOrStudy: string) => UnifiedContractStudyOption | null;
} {
  let me: ReturnType<typeof AuthModule.useMe> | null = null;
  try {
    me = AuthModule.useMe();
  } catch {
    me = null;
  }
  const greenfield = me ? isGreenfieldAdmin(me) : false;
  const studiesQuery = useStudies();

  const contracts = useMemo<readonly UnifiedContractStudyOption[]>(() => {
    const rawStudies = Array.isArray(studiesQuery.data) ? studiesQuery.data : [];
    const filteredStudies = greenfield
      ? rawStudies.filter((s) => !SEEDED_DEMO_STUDY_IDS.has(s.id))
      : rawStudies;

    const mappedFromStudies: UnifiedContractStudyOption[] = filteredStudies.map((st) => {
      const normalizedContractCode = st.code.startsWith('CTA-') ? st.code : `CTA-${st.code}`;
      const normalizedStudyCode = st.code.replace(/^CTA-/, '');
      const knownDemo = !greenfield
        ? DEMO_CONTRACTS_CATALOG.find(
            (d) =>
              d.contractCode.toUpperCase() === normalizedContractCode.toUpperCase() ||
              d.studyCode.toUpperCase() === normalizedStudyCode.toUpperCase(),
          )
        : undefined;
      return {
        contractCode: normalizedContractCode,
        studyCode: normalizedStudyCode,
        title: st.title,
        sponsorName: st.sponsorName || knownDemo?.sponsorName || '',
        croName: knownDemo?.croName ?? '',
        centerName: knownDemo?.centerName ?? 'Curitiba/PR',
        localityCityUf: knownDemo?.localityCityUf ?? 'Curitiba/PR',
        hourlyRateBrl: knownDemo?.hourlyRateBrl ?? 0,
      };
    });

    if (greenfield) {
      return mappedFromStudies;
    }

    const seen = new Set(mappedFromStudies.map((c) => c.contractCode));
    return [
      ...DEMO_CONTRACTS_CATALOG.filter((d) => !seen.has(d.contractCode)),
      ...mappedFromStudies,
    ];
  }, [greenfield, studiesQuery.data]);

  const resolveContract = (codeOrStudy: string): UnifiedContractStudyOption | null => {
    const clean = codeOrStudy.trim().toUpperCase();
    if (!clean) return contracts[0] ?? null;
    return (
      contracts.find(
        (c) =>
          c.contractCode.toUpperCase() === clean ||
          c.studyCode.toUpperCase() === clean ||
          `CTA-${c.studyCode.toUpperCase()}` === clean,
      ) ??
      contracts[0] ??
      null
    );
  };

  return {
    contracts,
    greenfield,
    resolveContract,
  };
}

export type SharedSupplyUnit =
  | 'UNIDADE'
  | 'CAIXA'
  | 'AMPOLA'
  | 'FRASCO'
  | 'LITRO'
  | 'ML'
  | 'KIT'
  | 'PAR';

export interface SharedRegisteredSupplyItem {
  readonly skuCode: string;
  readonly name: string;
  readonly category: string;
  readonly characteristicsSummary: string;
  readonly availableUnits: readonly SharedSupplyUnit[];
  readonly defaultUnit: SharedSupplyUnit;
  readonly unitPricesBrl: Readonly<Partial<Record<SharedSupplyUnit, number>>>;
  readonly localityPurchaseCostBrl: Readonly<Record<string, number>>;
  readonly localitySuggestedPricesBrl: Readonly<Record<string, number>>;
  readonly profitMarginPercent: number;
  readonly supplierName: string;
  readonly totalBalanceLabel: string;
}

const PRODUCTS_STORAGE_KEY = 'giyus.registered_products_catalog.v1';
let userRegisteredProducts: readonly SharedRegisteredSupplyItem[] = loadProductsFromStorage();
const productListeners = new Set<() => void>();

function loadProductsFromStorage(): readonly SharedRegisteredSupplyItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(PRODUCTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SharedRegisteredSupplyItem[]) : [];
  } catch {
    return [];
  }
}

function subscribeProducts(listener: () => void): () => void {
  productListeners.add(listener);
  return () => {
    productListeners.delete(listener);
  };
}

function getProductsSnapshot(): readonly SharedRegisteredSupplyItem[] {
  return userRegisteredProducts;
}

export function registerSharedProductOrSupply(
  item: SharedRegisteredSupplyItem,
): readonly SharedRegisteredSupplyItem[] {
  const filtered = userRegisteredProducts.filter(
    (p) => p.skuCode.toUpperCase() !== item.skuCode.toUpperCase(),
  );
  userRegisteredProducts = [item, ...filtered];
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(PRODUCTS_STORAGE_KEY, JSON.stringify(userRegisteredProducts));
    } catch {
      // Ignore storage quota errors
    }
  }
  for (const listener of productListeners) {
    listener();
  }
  return userRegisteredProducts;
}

export function useSharedRegisteredProducts(): {
  readonly userProducts: readonly SharedRegisteredSupplyItem[];
  readonly registerProduct: (
    item: SharedRegisteredSupplyItem,
  ) => readonly SharedRegisteredSupplyItem[];
} {
  const userProducts = useSyncExternalStore(
    subscribeProducts,
    getProductsSnapshot,
    getProductsSnapshot,
  );
  return {
    userProducts,
    registerProduct: registerSharedProductOrSupply,
  };
}
